import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import {
  fetchWishlist,
  removeFromWishlist,
  voteOnGame,
  promoteToPlaying,
  promoteToPlayed,
  markOnDeck,
  unmarkOnDeck,
  reorderOnDeck,
} from '@/api/wishlist'
import { fetchGroupSummary } from '@/api/groups'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { toFriend } from '@/lib/steam'
import { Icon } from '@/components/ui/Icon'
import { Spinner } from '@/components/ui/Spinner'
import { ThemedSelect } from '@/components/ui/ThemedSelect'
import { WishlistRow } from '@/components/wishlist/WishlistRow'
import { AddGameModal } from '@/components/wishlist/AddGameModal'
import { GameDetailModal } from '@/components/wishlist/GameDetailModal'
import { GroupHeader } from '@/components/groups/GroupHeader'
import type { WishlistEntryWithGame, WishlistResponse, Friend } from '@/types'

type OwnerFilter = 'all' | 'nobody' | 'someone' | 'sale'
type SortBy = 'votes' | 'price' | 'rating' | 'added' | 'name' | 'owners'
type SortDir = 'asc' | 'desc'
const COLLAPSED_TAG_LIMIT = 12
const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'votes', label: 'Most votes' },
  { value: 'price', label: 'Lowest price' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'added', label: 'Recently added' },
  { value: 'name', label: 'Alphabetical' },
]
const DEFAULT_SORT_DIR: Record<SortBy, SortDir> = {
  votes: 'desc',
  price: 'asc',
  rating: 'desc',
  added: 'desc',
  name: 'asc',
  owners: 'desc',
}

function netVotes(entry: WishlistEntryWithGame): number {
  return Object.values(entry.votes).reduce((s, v) => s + v, 0)
}

interface SortHeaderBtnProps {
  col: SortBy
  activeCol: SortBy
  dir: SortDir
  onClick: (col: SortBy) => void
  className?: string
  children: ReactNode
}

function SortHeaderBtn({ col, activeCol, dir, onClick, className, children }: SortHeaderBtnProps) {
  const isActive = col === activeCol
  return (
    <button
      className={['cg-sort-btn', className].filter(Boolean).join(' ')}
      data-active={isActive}
      onClick={() => onClick(col)}
      title={isActive ? (dir === 'asc' ? 'Ascending — click to reverse' : 'Descending — click to reverse') : 'Click to sort'}
    >
      {children}
      {isActive && (
        <Icon
          name={dir === 'asc' ? 'collapse' : 'expand'}
          size={10}
          className="cg-sort-indicator"
        />
      )}
    </button>
  )
}

interface ReorderableRowProps {
  value: string
  onCommit: () => void
  canEdit: boolean
  children: (dragHandle: ReactNode) => ReactNode
}

// Wraps an on-deck row so it can be dragged by its grip handle only — the row
// body stays clickable (expand) since dragListener is disabled. Persists the new
// order once, on drag end, rather than on every reorder tick. Without edit
// rights the row renders in place with no handle (no reordering).
function ReorderableRow({ value, onCommit, canEdit, children }: ReorderableRowProps) {
  const controls = useDragControls()
  if (!canEdit) {
    return <div>{children(null)}</div>
  }
  const handle = (
    <button
      type="button"
      className="cg-drag-handle"
      title="Drag to reorder"
      onPointerDown={(e) => controls.start(e)}
      onClick={(e) => e.stopPropagation()}
      aria-label="Drag to reorder"
    >
      <Icon name="grip" size={14} />
    </button>
  )
  return (
    <Reorder.Item
      as="div"
      value={value}
      dragListener={false}
      dragControls={controls}
      onDragEnd={() => onCommit()}
    >
      {children(handle)}
    </Reorder.Item>
  )
}

export default function WishlistPage() {
  const { user } = useAuthStore()
  const { activeGroupId } = useUIStore()
  const { canEditContent } = useGroupPermissions()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all')
  const [sortBy, setSortBy] = useState<SortBy>('votes')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set())
  const [showAllTags, setShowAllTags] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [detailGameId, setDetailGameId] = useState<string | null>(null)
  const [onDeckIds, setOnDeckIds] = useState<string[]>([])

  const { data: wishlistData, isLoading } = useQuery({
    queryKey: ['wishlist', activeGroupId],
    queryFn: () => fetchWishlist(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const groupMembers: Friend[] = useMemo(
    () => (summary?.members ?? [])
      .map(toFriend)
      .sort((a, b) => (a.nickname || a.name).localeCompare(b.nickname || b.name)),
    [summary],
  )

  const voteMutation = useMutation({
    mutationFn: ({ gameId, vote }: { gameId: string; vote: 1 | -1 | 0 }) =>
      voteOnGame(activeGroupId!, gameId, vote),
    // optimistic update
    onMutate: async ({ gameId, vote }) => {
      if (!activeGroupId) return { previous: null }
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])

      queryClient.setQueryData(['wishlist', activeGroupId], (old: { entries: { gameId: string, votes: Record<string, 1 | -1> }[] }) => {
        if (!old) return old
        const entries = old.entries.map((e) => {
          if (e.gameId !== gameId) return e
          const nextVotes = { ...e.votes }
          if (vote === 0) {
            delete nextVotes[user!.steamId]
          } else {
            nextVotes[user!.steamId] = vote
          }
          return { ...e, votes: nextVotes }
        })
        return { ...old, entries }
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (!activeGroupId) return
      if (context?.previous) {
        queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
      }
    },
    onSettled: () => {
      if (!activeGroupId) return
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
    },
  })

  const removeMutation = useMutation({
    mutationFn: (gameId: string) => removeFromWishlist(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      queryClient.setQueryData(['wishlist', activeGroupId], (old: WishlistResponse) =>
        old ? { ...old, entries: old.entries.filter((e) => e.gameId !== gameId) } : old
      )
      setExpandedId(null)
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] }),
  })

  const promoteToPlayingMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlaying(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      queryClient.setQueryData(['wishlist', activeGroupId], (old: WishlistResponse) =>
        old ? { ...old, entries: old.entries.filter((e) => e.gameId !== gameId) } : old
      )
      setExpandedId(null)
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
    },
  })

  const promoteToPlayedMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlayed(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      queryClient.setQueryData(['wishlist', activeGroupId], (old: WishlistResponse) =>
        old ? { ...old, entries: old.entries.filter((e) => e.gameId !== gameId) } : old
      )
      setExpandedId(null)
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
    },
  })

  // Optimistically patch a single entry's onDeckRank in the wishlist cache.
  function patchOnDeckRank(gameId: string, rank: number | null) {
    queryClient.setQueryData(['wishlist', activeGroupId], (old: WishlistResponse | undefined) =>
      old
        ? {
            ...old,
            entries: old.entries.map((e) =>
              e.gameId === gameId ? { ...e, onDeckRank: rank } : e,
            ),
          }
        : old,
    )
  }

  const markOnDeckMutation = useMutation({
    mutationFn: (gameId: string) => markOnDeck(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      const maxRank = (queryClient.getQueryData(['wishlist', activeGroupId]) as WishlistResponse | undefined)
        ?.entries.reduce((m, e) => Math.max(m, e.onDeckRank ?? -1), -1) ?? -1
      patchOnDeckRank(gameId, maxRank + 1)
      return { previous }
    },
    onError: (_err, _gameId, context) => {
      if (context?.previous) queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['group-summary', activeGroupId] })
    },
  })

  const unmarkOnDeckMutation = useMutation({
    mutationFn: (gameId: string) => unmarkOnDeck(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      patchOnDeckRank(gameId, null)
      return { previous }
    },
    onError: (_err, _gameId, context) => {
      if (context?.previous) queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['group-summary', activeGroupId] })
    },
  })

  const reorderOnDeckMutation = useMutation({
    mutationFn: (gameIds: string[]) => reorderOnDeck(activeGroupId!, gameIds),
    onMutate: async (gameIds) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist', activeGroupId] })
      const previous = queryClient.getQueryData(['wishlist', activeGroupId])
      const rankByGame = new Map(gameIds.map((id, i) => [id, i]))
      queryClient.setQueryData(['wishlist', activeGroupId], (old: WishlistResponse | undefined) =>
        old
          ? {
              ...old,
              entries: old.entries.map((e) =>
                rankByGame.has(e.gameId) ? { ...e, onDeckRank: rankByGame.get(e.gameId)! } : e,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _gameIds, context) => {
      if (context?.previous) queryClient.setQueryData(['wishlist', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] }),
  })

  useEffect(() => {
    setShowAllTags(false)
  }, [activeGroupId])

  // Server-truth on-deck order (gameIds ascending by rank). Local onDeckIds
  // mirrors this but lets drags reorder instantly before the server confirms.
  const serverOnDeckIds = useMemo(
    () =>
      (wishlistData?.entries ?? [])
        .filter((e) => e.onDeckRank != null)
        .sort((a, b) => (a.onDeckRank ?? 0) - (b.onDeckRank ?? 0))
        .map((e) => e.gameId),
    [wishlistData],
  )

  useEffect(() => {
    setOnDeckIds((prev) => (prev.join(',') === serverOnDeckIds.join(',') ? prev : serverOnDeckIds))
  }, [serverOnDeckIds])

  const onDeckIdsRef = useRef(onDeckIds)
  onDeckIdsRef.current = onDeckIds

  const entries = wishlistData?.entries ?? []
  const ownership = wishlistData?.ownership ?? {}
  const steamWishlists = wishlistData?.steamWishlists ?? {}

  // Collect tags by frequency so the rail favors the group's most common themes.
  const allTags = useMemo(() => {
    const counts = new Map<string, number>()
    entries.forEach((entry) => {
      new Set(entry.game.tags).forEach((tag) => {
        counts.set(tag, (counts.get(tag) ?? 0) + 1)
      })
    })

    return [...counts.entries()]
      .sort(([tagA, countA], [tagB, countB]) => countB - countA || tagA.localeCompare(tagB))
      .map(([tag]) => tag)
  }, [entries])
  const visibleTags = showAllTags
    ? allTags
    : allTags.filter((tag, index) => index < COLLAPSED_TAG_LIMIT || activeTags.has(tag))
  const hiddenTagCount = allTags.length - visibleTags.length
  const canToggleTags = showAllTags || hiddenTagCount > 0

  if (!activeGroupId) {
    return (
      <div className="cg-screen">
        <div className="cg-empty">Select or create a group to see its wishlist.</div>
      </div>
    )
  }

  // Build a member lookup by steam ID
  const memberById = new Map(groupMembers.map((m) => [m.id, m]))
  // Build an existing game IDs set for the modal
  const existingGameIds = new Set(entries.map((e) => e.gameId))

  // Pin the On Deck group at the top only in the default view; once the user is
  // searching/filtering, on-deck games fall back into the normal filtered list so
  // filters behave intuitively.
  const isDefaultView = !search && ownerFilter === 'all' && activeTags.size === 0
  const showOnDeckGroup = isDefaultView && onDeckIds.length > 0
  const entryById = new Map(entries.map((e) => [e.gameId, e]))

  // Filter
  const filtered = entries.filter((entry) => {
    if (showOnDeckGroup && entry.onDeckRank != null) return false
    const q = search.toLowerCase()
    if (q && !entry.game.title.toLowerCase().includes(q) && !entry.game.tags.some((t) => t.toLowerCase().includes(q))) {
      return false
    }
    if (activeTags.size > 0 && !entry.game.tags.some((t) => activeTags.has(t))) {
      return false
    }
    if (ownerFilter === 'nobody' && (ownership[entry.gameId] ?? []).length > 0) return false
    if (ownerFilter === 'someone' && (ownership[entry.gameId] ?? []).length === 0) return false
    if (ownerFilter === 'sale' && !entry.game.sale) return false
    return true
  })

  // Sort — cmp convention: positive = a after b (ascending natural order)
  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0
    switch (sortBy) {
      case 'votes': {
        const voteDiff = netVotes(a) - netVotes(b)
        if (voteDiff !== 0) { cmp = voteDiff; break }
        // stable tie-break: newest addition first (always)
        return new Date(b.addedAt ?? 0).getTime() - new Date(a.addedAt ?? 0).getTime()
      }
      case 'name': cmp = a.game.title.localeCompare(b.game.title); break
      case 'price': cmp = (a.game.sale ?? a.game.price) - (b.game.sale ?? b.game.price); break
      case 'rating': cmp = a.game.rating - b.game.rating; break
      case 'owners': cmp = (ownership[a.gameId] ?? []).length - (ownership[b.gameId] ?? []).length; break
      case 'added': cmp = new Date(a.addedAt ?? 0).getTime() - new Date(b.addedAt ?? 0).getTime(); break
    }
    return sortDir === 'asc' ? cmp : -cmp
  })
  const sortKey = sorted.map((e) => e.gameId).join(',')

  function toggleTag(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  function handleColumnSort(col: SortBy) {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortDir(DEFAULT_SORT_DIR[col])
    }
  }

  function handleDropdownSort(col: SortBy) {
    setSortBy(col)
    setSortDir(DEFAULT_SORT_DIR[col])
  }

  function toggleOnDeck(entry: WishlistEntryWithGame) {
    if (entry.onDeckRank != null) unmarkOnDeckMutation.mutate(entry.gameId)
    else markOnDeckMutation.mutate(entry.gameId)
  }

  function commitReorder() {
    reorderOnDeckMutation.mutate(onDeckIdsRef.current)
  }

  return (
    <div className="cg-screen">
      <GroupHeader sublabel={entries.length > 0 ? `${entries.length} game${entries.length !== 1 ? 's' : ''}` : undefined} />

      {/* Toolbar */}
      <div className="cg-toolbar">
        <div className="cg-search">
          <Icon name="search" size={14} />
          <input
            placeholder="Search games…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="cg-search-clear" onClick={() => setSearch('')}>
              <Icon name="x" size={10} />
            </button>
          )}
        </div>

        <div className="cg-toolbar-divider" />

        <div className="cg-filter-group">
          <span className="cg-filter-label">Owns</span>
          <div className="cg-segment">
            {(['all', 'nobody', 'someone', 'sale'] as OwnerFilter[]).map((f) => (
              <button
                key={f}
                className="cg-seg-btn"
                data-active={ownerFilter === f}
                onClick={() => setOwnerFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'nobody' ? 'Nobody' : f === 'someone' ? 'Someone' : 'On sale'}
              </button>
            ))}
          </div>
        </div>

        <div className="cg-toolbar-divider" />

        <div className="cg-filter-group">
          <span className="cg-filter-label">Sort</span>
          <ThemedSelect value={sortBy} options={SORT_OPTIONS} onChange={handleDropdownSort} ariaLabel="Sort wishlist" />
        </div>

        <div className="cg-toolbar-spacer" />

        {canEditContent && (
          <button className="cg-btn cg-btn-primary" onClick={() => setShowAddModal(true)}>
            <Icon name="plus" size={12} />
            Add game
          </button>
        )}
      </div>

      {/* Tag rail */}
      {allTags.length > 0 && (
        <div className="cg-tag-rail">
          {visibleTags.map((tag) => (
            <button
              key={tag}
              className="cg-tag"
              data-active={activeTags.has(tag)}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
          {canToggleTags && (
            <button
              className="cg-tag cg-tag-more"
              onClick={() => setShowAllTags((showing) => !showing)}
              aria-expanded={showAllTags}
            >
              {showAllTags ? (
                <>
                  <Icon name="collapse" size={11} />
                  Show fewer
                </>
              ) : (
                <>
                  <Icon name="expand" size={11} />
                  {hiddenTagCount} more
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* On Deck — pinned group of games queued to play next */}
      {showOnDeckGroup && (
        <div className="cg-ondeck-group">
          <div className="cg-ondeck-header">
            <Icon name="bookmark" size={13} />
            <span className="cg-ondeck-title">On Deck</span>
            <span className="cg-ondeck-count">{onDeckIds.length}</span>
            {canEditContent && onDeckIds.length > 1 && <span className="cg-ondeck-hint">drag to reorder</span>}
          </div>
          <Reorder.Group
            as="div"
            axis="y"
            values={onDeckIds}
            onReorder={setOnDeckIds}
            className="cg-list"
          >
            {onDeckIds.map((id) => {
              const entry = entryById.get(id)
              if (!entry) return null
              return (
                <ReorderableRow key={id} value={id} onCommit={commitReorder} canEdit={canEditContent}>
                  {(dragHandle) => (
                    <WishlistRow
                      entry={entry}
                      ownership={ownership}
                      steamWishlistedBy={steamWishlists[id]}
                      groupMembers={groupMembers}
                      currentUser={user!}
                      addedByUser={memberById.get(entry.addedBy)}
                      isExpanded={expandedId === id}
                      onToggleExpand={() => setExpandedId(expandedId === id ? null : id)}
                      onVote={(vote) => voteMutation.mutate({ gameId: id, vote })}
                      onRemove={() => removeMutation.mutate(id)}
                      onViewDetails={() => setDetailGameId(id)}
                      onPromoteToPlaying={() => promoteToPlayingMutation.mutate(id)}
                      onPromoteToPlayed={() => promoteToPlayedMutation.mutate(id)}
                      isOnDeck
                      onToggleOnDeck={() => toggleOnDeck(entry)}
                      dragHandle={dragHandle}
                      canEdit={canEditContent}
                    />
                  )}
                </ReorderableRow>
              )
            })}
          </Reorder.Group>
        </div>
      )}

      {/* List header */}
      {entries.length > 0 && (
        <div className="cg-list-header">
          <div className="cg-col-cover" />
          <SortHeaderBtn col="name" activeCol={sortBy} dir={sortDir} onClick={handleColumnSort}>Game</SortHeaderBtn>
          <SortHeaderBtn col="rating" activeCol={sortBy} dir={sortDir} onClick={handleColumnSort} className="cg-col-rating">Rating</SortHeaderBtn>
          <SortHeaderBtn col="owners" activeCol={sortBy} dir={sortDir} onClick={handleColumnSort} className="cg-col-owners">Owners</SortHeaderBtn>
          <SortHeaderBtn col="votes" activeCol={sortBy} dir={sortDir} onClick={handleColumnSort}>Votes</SortHeaderBtn>
          <SortHeaderBtn col="price" activeCol={sortBy} dir={sortDir} onClick={handleColumnSort} className="cg-col-price">Price</SortHeaderBtn>
          <div />
        </div>
      )}

      {/* Rows */}
      <div className="cg-list">
        {isLoading && <Spinner />}
        {!isLoading && entries.length === 0 && (
          <div className="cg-empty">
            {canEditContent ? (
              <>
                No games yet —{' '}
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 'inherit' }}
                  onClick={() => setShowAddModal(true)}
                >
                  add one!
                </button>
              </>
            ) : (
              'No games yet.'
            )}
          </div>
        )}
        {!isLoading && entries.length > 0 && sorted.length === 0 && (
          <div className="cg-empty">No games match the current filters.</div>
        )}
        <AnimatePresence initial={false}>
          {sorted.map((entry) => (
            <motion.div
              key={entry.gameId}
              layout="position"
              layoutDependency={sortKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                layout: { type: 'spring', stiffness: 400, damping: 35 },
                opacity: { duration: 0.15 },
              }}
            >
              <WishlistRow
                entry={entry}
                ownership={ownership}
                steamWishlistedBy={steamWishlists[entry.gameId]}
                groupMembers={groupMembers}
                currentUser={user!}
                addedByUser={memberById.get(entry.addedBy)}
                isExpanded={expandedId === entry.gameId}
                onToggleExpand={() => setExpandedId(expandedId === entry.gameId ? null : entry.gameId)}
                onVote={(vote) => voteMutation.mutate({ gameId: entry.gameId, vote })}
                onRemove={() => removeMutation.mutate(entry.gameId)}
                onViewDetails={() => setDetailGameId(entry.gameId)}
                onPromoteToPlaying={() => promoteToPlayingMutation.mutate(entry.gameId)}
                onPromoteToPlayed={() => promoteToPlayedMutation.mutate(entry.gameId)}
                isOnDeck={entry.onDeckRank != null}
                onToggleOnDeck={() => toggleOnDeck(entry)}
                canEdit={canEditContent}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {showAddModal && activeGroupId && (
        <AddGameModal
          groupId={activeGroupId}
          existingGameIds={existingGameIds}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {detailGameId && (() => {
        const detailEntry = entries.find((e) => e.gameId === detailGameId)
        return detailEntry ? (
          <GameDetailModal
            game={detailEntry.game}
            groupSize={groupMembers.length}
            onClose={() => setDetailGameId(null)}
          />
        ) : null
      })()}
    </div>
  )
}
